/*
 * Copyright (c) 2016-2023 VMware, Inc. All Rights Reserved.
 * This software is released under MIT license.
 * The full license information can be found in LICENSE in the root directory of this project.
 */
import { getOffesetDifference } from '../utils/math.js';
/**
 * @internal Provides all nessesary events for basic touch gestures
 *
 * @event cdsTouchStart
 * @event cdsTouchMove
 * @event cdsTouchEnd
 */
export function touch() {
    return (target) => target.addInitializer((instance) => new TouchController(instance));
}
export class TouchController {
    constructor(host) {
        this.host = host;
        this.moveHandler = this.move.bind(this);
        this.endHandler = this.end.bind(this);
        this.host.addController(this);
    }
    async hostConnected() {
        await this.host.updateComplete;
        this.host.addEventListener('pointerdown', (e) => this.start(e), { passive: true });
    }
    start(e) {
        if (e.composedPath().find((el) => el === this.host)) {
            this.startPosition = { x: e.clientX, y: e.clientY };
            document.addEventListener('pointerup', this.endHandler, { passive: true });
            document.addEventListener('pointermove', this.moveHandler, { passive: true });
            this.host.dispatchEvent(new CustomEvent('cdsTouchStart', { detail: { ...this.startPosition } }));
        }
    }
    end(e) {
        if (this.startPosition) {
            const detail = this.getCoordinates(e);
            document.removeEventListener('pointerup', this.endHandler, false);
            document.removeEventListener('pointermove', this.moveHandler, false);
            this.host.dispatchEvent(new CustomEvent('cdsTouchEnd', { detail }));
        }
    }
    move(e) {
        requestAnimationFrame(() => {
            const detail = this.getCoordinates(e);
            this.startPosition = { x: e.clientX, y: e.clientY };
            this.host.dispatchEvent(new CustomEvent('cdsTouchMove', { detail }));
        });
    }
    getCoordinates(e) {
        return {
            x: e.clientX,
            y: e.clientY,
            offsetX: getOffesetDifference(this.startPosition.x, e.clientX),
            offsetY: getOffesetDifference(this.startPosition.y, e.clientY),
        };
    }
}
